import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  linkedSignal,
  OnInit,
  Output,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AddressDTO,
  API,
  AttributeMapDTO,
  AttributeType,
  CompanyDTO,
  CompanyFullDTO,
  CullDTO,
  LocationDTO,
  LocationFullDTO,
  LocationOnlineStatus,
  Order,
  PlayerCullDTO,
  PoolDTO,
  ReportingFullDTO,
  ss_fm_Location_export_filter,
  ss_fm_Location_export_order,
  ss_fm_Location_retrieveAll_filter,
  ss_fm_Location_retrieveAll_order,
  WeeklyScheduleDTO,
} from 'src/app/services/api/api';
import { ErrorService } from '../../../services/error.service';
import { forkJoin, from, of, switchMap } from 'rxjs';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TagService } from '../../../services/tag.service';
import { LocationFullDTOAndTags } from '../../../models/taggable.model';
import {
  IDAndTypeInterface,
  OrderOption,
  SbLocationListColumn,
  SbSplitButton,
  SelectionState,
} from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReportingModalComponent } from '../../../components/standard-reporting/reporting-modal/reporting-modal.component';
import { ReportingService } from '../../../services/reporting.service';
import { createAttributeColumns, getPropsFromDTO, isColumnMatch } from '../../../services/global/global-methods';
import { clientColumnDefaultsLocations } from './clientColumnDefaultsLocations';
import { DynamicTableBase } from '../../../components/dynamic-table-elements/DynamicTableBase';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { MultiSelectButtonAction } from '../../../components/table-multi-select-button/table-multi-select-button.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { FilterPanelComponent } from '@ngx-seatback/layout';

type SplitButtonAction = 'delete';
type LocationFullDTOAndTagsAndButtons = LocationFullDTOAndTags & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationsComponent extends DynamicTableBase<SbLocationListColumn> implements OnInit, AfterViewInit, ModalNavigationComponent {
  // injects
  protected readonly api = inject(API);
  private readonly modalNavigation = inject(ModalNavigationService);
  protected readonly util = inject(UtilService);
  private readonly errorHandler = inject(ErrorService);
  protected readonly translate = inject(TranslateService);
  protected readonly tagService = inject(TagService);
  protected readonly reportingService = inject(ReportingService);
  protected readonly permissionService = inject(PermissionService);
  // global reference imports
  protected readonly window = window;
  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  @Input() isInModalContext: boolean = false;
  @Input() selectionMode: boolean = false;

  @Output() elementSelected: EventEmitter<LocationFullDTOAndTags> = new EventEmitter<LocationFullDTOAndTags>();

  /* filter variables */
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  // Id filtering
  filteredSchedules: WeeklyScheduleDTO[] = [];
  filteredManagers: CompanyDTO[] = [];
  filteredCulls: CullDTO[] = [];
  // ThreeWay-toggles
  hasDevices: boolean | null = null;
  hasManager: boolean | null = null;
  hasPoint: boolean | null = null;
  // Location Online Status
  online: boolean = true;
  mixed: boolean = true;
  offline: boolean = true;
  unknown: boolean = true;

  /* ordering */
  orderOption = signal<OrderOption<SbLocationListColumn>>({
    direction: Order.asc,
    value: {
      id: 'name',
      title: 'lang.globals.name',
      selected: false,

      type: 'PROPERTY',
      orderId: ss_fm_Location_retrieveAll_order.NAME,
      canOrder: true,
      filterId: ss_fm_Location_retrieveAll_filter.NAME,
      canFilter: true,
    },
  });
  filterPanelOrderOption = linkedSignal<OrderOption<SbLocationListColumn>, OrderOption<SbLocationListColumn>>({
    source: this.orderOption,
    computation: orderOption => {
      return { ...orderOption };
    },
  });
  allOrderOptions: OrderOption<SbLocationListColumn>[] = [];

  // export
  exportLoading: boolean = false;
  isSelectedLocationsExport: boolean = false;
  form: FormGroup = new FormGroup({
    Title: new FormControl('', [Validators.required]),
  });

  /* global */
  showSideBar: boolean = false;
  locations = signal<LocationFullDTOAndTagsAndButtons[]>([]);
  selectedLocations = signal<Map<number, LocationFullDTOAndTagsAndButtons>>(new Map());
  selectionState: Signal<SelectionState> = computed(() => {
    const currentlySelectedLocations = this.selectedLocations();
    const currentLocations = this.locations();
    if (!currentLocations || currentLocations.length === 0) {
      return 'none';
    }
    const allSelectedOnCurrentPage = currentLocations.every(item => currentlySelectedLocations.has(item.id));
    if (allSelectedOnCurrentPage) {
      return 'all';
    }
    const someSelectedOnCurrentPage = currentLocations.some(item => currentlySelectedLocations.has(item.id));
    if (someSelectedOnCurrentPage) {
      return 'partly';
    }
    return 'none';
  });

  constructor() {
    super();
    // has to be called before resetOrderOption to initialize columns
    this.columnDefaults =
      this.util.getSeatbackCMSClient() === 'pmg' ? clientColumnDefaultsLocations['pmg'] : clientColumnDefaultsLocations['generic'];
    this.resetOrderOption();
  }

  ngOnInit() {
    const attributeTypes: AttributeType[] = [];
    if (this.permissionService.hasPermission(this.permissionService.Permission.location_attribute_datatype_list)) {
      attributeTypes.push(AttributeType.Location);
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.company_attribute_datatype_list)) {
      attributeTypes.push(AttributeType.Company);
    }

    of(attributeTypes)
      .pipe(
        switchMap((types: AttributeType[]) =>
          forkJoin({
            attributeMap: this.api.shared_Attribute_retrieve({ type: types }),
            preselectedColumns: from(this.loadSelectedColumns('locationListSelectedColumnsV1')),
          })
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (forkJoinResult: { attributeMap: AttributeMapDTO; preselectedColumns: IDAndTypeInterface[] | null }) => {
          this.initColumns(forkJoinResult.attributeMap, forkJoinResult.preselectedColumns);
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ngAfterViewInit() {
    // subscription for filterValue deselect
    this.initTagSubscription();
  }

  // order methods
  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({
      value: this.columns.find(col => col.id === 'name'),
      direction: Order.asc,
    });
  }

  setToOrderAndRefresh(pValue: SbLocationListColumn) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  private setToOrderOption(pValue: SbLocationListColumn) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ value: pValue, direction: Order.asc });
    }
  }

  getOrderOptions(): { by: ss_fm_Location_retrieveAll_order; param?: string; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value?.type) {
      this.resetOrderOption();
      return {
        by: ss_fm_Location_retrieveAll_order.NAME,
        direction: Order.asc,
      };
    }

    // if value equals column title
    switch (currentOrderOption.value.type) {
      case 'LOCATION_ATTRIBUTE':
        return {
          by: ss_fm_Location_retrieveAll_order.ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'COMPANY_ATTRIBUTE':
        return {
          by: ss_fm_Location_retrieveAll_order.MANAGED_BY_ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'PROPERTY':
        return {
          by: currentOrderOption.value.orderId as ss_fm_Location_retrieveAll_order,
          direction: currentOrderOption.direction,
        };
      default:
        this.resetOrderOption();
        return {
          by: ss_fm_Location_retrieveAll_order.NAME,
          direction: Order.asc,
        };
    }
  }

  getExportOrderOptions(): { by: ss_fm_Location_export_order; param?: string; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value?.type) {
      this.resetOrderOption();
      return {
        by: ss_fm_Location_export_order.NAME,
        direction: Order.asc,
      };
    }

    // if value equals column title
    switch (currentOrderOption.value.type) {
      case 'LOCATION_ATTRIBUTE':
        return {
          by: ss_fm_Location_export_order.ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'COMPANY_ATTRIBUTE':
        return {
          by: ss_fm_Location_export_order.MANAGED_BY_ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'PROPERTY':
        return {
          by: currentOrderOption.value.orderId as ss_fm_Location_export_order,
          direction: currentOrderOption.direction,
        };
      default:
        this.resetOrderOption();
        return {
          by: ss_fm_Location_export_order.NAME,
          direction: Order.asc,
        };
    }
  }

  isFilterClassActive(column: SbLocationListColumn): boolean {
    if (column.type === 'PROPERTY') {
      const id = column.id;
      const canFilter = column?.canFilter;

      const propertyFilters = {
        // pool: this.filteredPools?.length > 0,
        // location: this.filteredLocations?.length > 0,
        // channel: this.filteredChannels?.length > 0,
        default: canFilter && this.columnFiltersMap?.has(id),
      };

      return propertyFilters[id] ?? propertyFilters.default;
    }

    const filterValue = this.columnFiltersMap?.get(column.id);

    return this.stringTransformation.stringNotNull(filterValue?.value);
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const schedules = this.filteredSchedules?.length > 0 ? this.filteredSchedules.map(item => item.id) : null;
    const managers = this.filteredManagers?.length > 0 ? this.filteredManagers.map(item => item.id) : null;
    const culls = this.filteredCulls?.length > 0 ? this.filteredCulls.map(item => item.id) : null;
    const sub = this.api
      .ss_fm_Location_retrieveAll(
        {
          playerCull: culls,
          hasDevices: this.hasDevices,
          hasPoint: this.hasPoint,
          hasManager: this.hasManager,
          filter: this.getFilterOptions(),
          manager: managers,
          onlineStatus: this.getLocationOnlineStatus(),
          order: this.getOrderOptions(),
          serviceHour: schedules,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        switchMap(locs => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Location, locs)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (e: LocationFullDTO[]) => {
          this.locations.set(this.buildSplitButtons(e));
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  export() {
    this.loading.set(true);
    this.exportLoading = true;
    this.showSideBar = false;
    const schedules = this.filteredSchedules?.length > 0 ? this.filteredSchedules.map(item => item.id) : null;
    const managers = this.filteredManagers?.length > 0 ? this.filteredManagers.map(item => item.id) : null;
    const culls = this.filteredCulls?.length > 0 ? this.filteredCulls.map(item => item.id) : null;
    this.api
      .ss_fm_Location_export({
        playerCull: culls,
        hasDevices: this.hasDevices,
        hasPoint: this.hasPoint,
        hasManager: this.hasManager,
        filter: this.getExportFilterOptions(),
        manager: managers,
        onlineStatus: this.getLocationOnlineStatus(),
        order: this.getExportOrderOptions(),
        serviceHour: schedules,
        search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        reportName: this.form.get('Title').value ?? null,
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (rep: ReportingFullDTO) => {
          this.reportingService.addReport(rep);
          this.modalService
            .showNavigationModal({
              entryComponent: {
                value: ReportingModalComponent,
                data: rep,
              },
              title: this.translate.instant('lang.globals.download'),
              modalOptions: { windowClass: 'M' },
            })
            .then(() => {
              this.clearExportModal();
            })
            .catch(() => {
              this.clearExportModal();
            });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  clearExportModal() {
    this.loading.set(false);
    this.exportLoading = false;
  }

  getFilterOptions(): { by: ss_fm_Location_retrieveAll_filter; param?: string; values: string[] }[] | null {
    const ret: { by: ss_fm_Location_retrieveAll_filter; param?: string; values: string[] }[] = [];

    if (!this.isFilterEnabled()) {
      return null;
    }

    for (const [columnId, columnFilterValue] of this.columnFiltersMap.entries()) {
      switch (columnFilterValue?.column?.type) {
        case 'LOCATION_ATTRIBUTE':
          ret.push({
            by: ss_fm_Location_retrieveAll_filter.ATTRIBUTES,
            param: columnId,
            values: ['%' + columnFilterValue.value + '%'],
          });
          break;
        case 'COMPANY_ATTRIBUTE':
          ret.push({
            by: ss_fm_Location_retrieveAll_filter.MANAGED_BY_ATTRIBUTES,
            param: columnId,
            values: ['%' + columnFilterValue.value + '%'],
          });

          break;
        case 'PROPERTY':
          if (columnFilterValue?.column?.filterId) {
            ret.push({
              by: columnFilterValue?.column?.filterId as ss_fm_Location_retrieveAll_filter,
              values: ['%' + columnFilterValue.value + '%'],
            });
          }
          break;
      }
    }
    return ret.length > 0 ? ret : null;
  }

  getExportFilterOptions(): { by: ss_fm_Location_export_filter; param?: string; values: string[] }[] {
    const ret: { by: ss_fm_Location_export_filter; param?: string; values: string[] }[] = [];

    if (!this.isFilterEnabled()) {
      return null;
    }

    for (const [columnId, columnFilterValue] of this.columnFiltersMap.entries()) {
      switch (columnFilterValue?.column?.type) {
        case 'LOCATION_ATTRIBUTE':
          ret.push({
            by: ss_fm_Location_export_filter.ATTRIBUTES,
            param: columnId,
            values: ['%' + columnFilterValue.value + '%'],
          });
          break;
        case 'COMPANY_ATTRIBUTE':
          ret.push({
            by: ss_fm_Location_export_filter.MANAGED_BY_ATTRIBUTES,
            param: columnId,
            values: ['%' + columnFilterValue.value + '%'],
          });

          break;
        case 'PROPERTY':
          if (columnFilterValue?.column?.filterId) {
            ret.push({
              by: columnFilterValue?.column?.filterId as ss_fm_Location_export_filter,
              values: ['%' + columnFilterValue.value + '%'],
            });
          }
          break;
      }
    }
    return ret.length > 0 ? ret : null;
  }

  getLocationOnlineStatus(): LocationOnlineStatus[] {
    const arr: LocationOnlineStatus[] = [];

    if (this.online) {
      arr.push(LocationOnlineStatus.AllOnline, LocationOnlineStatus.Online);
    }
    if (this.mixed) {
      arr.push(LocationOnlineStatus.MostlyOnline, LocationOnlineStatus.MostlyOffline, LocationOnlineStatus.AllCurrentlyOffline);
    }
    if (this.offline) {
      arr.push(LocationOnlineStatus.Offline, LocationOnlineStatus.AllOffline);
    }
    if (this.unknown) {
      arr.push(LocationOnlineStatus.Unknown);
    }

    if (arr.length <= 0) {
      return;
    }

    return arr;
  }

  getManagerId(): string | undefined {
    if (this.filteredManagers && this.filteredManagers.length === 1) {
      return this.filteredManagers[0].id;
    }
    return undefined;
  }

  // deletion methods
  deleteLocation(location: LocationFullDTOAndTags) {
    if (!location) {
      throw new Error('Location not found!');
    }
    this.api.ss_fm_Location_retrieveDevicesByLocationId(location.id).subscribe({
      next: devices => {
        if (devices.length > 0) {
          this.showDeletionWarningModal();
        } else {
          this.showDeletionModal(location);
        }
      },
    });
  }

  private showDeletionModal(location: LocationFullDTOAndTags) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteElementValue', { value: location.name }),
        message: this.translate.instant('lang.globals.deleteElementValueQuestion', { value: location.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api.ss_fm_Location_deleteById(location.id).subscribe({
            next: () => {
              this.requestDebounced();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
        }
      })
      .catch(() => {});
  }

  private showDeletionWarningModal() {
    this.modalService
      .modal(
        {
          title: this.translate.instant('lang.globals.delete'),
          modalOptions: { windowClass: 'S' },
        },
        {
          templateId: 'deleteWarning',
        }
      )
      .then(() => {})
      .catch(() => {});
  }

  // Button methods
  buildSplitButtons<T extends { id: any; countDevices: number }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.location_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.locationDetails,
          pathParams: [{ id: 'locationID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.location_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.locationEdit,
          pathParams: [{ id: 'locationID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.device_list])) {
        item.sbButtons.push({
          icon: 'playerOnline',
          tooltip: 'lang.globals.openDeviceList',
          link: RoutingID.devices,
          queryParams: { locationIds: this.getObjectId(item), returnTo: this.util.getPageLink() },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.location_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
          disabled: item.countDevices > 0,
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(location: LocationFullDTOAndTagsAndButtons, $event: SplitButtonAction) {
    if ($event === 'delete') {
      this.deleteLocation(location);
    } else {
      console.error('Missing action implementation of Splitbutton');
    }
  }

  // column methods
  initColumns(attributeMap: AttributeMapDTO, preselectedColumns: IDAndTypeInterface[] | null) {
    this.columns = [];

    const allColumns: string[] = [
      ...getPropsFromDTO('', Object.keys(new LocationFullDTO())),
      ...getPropsFromDTO('address', Object.keys(new AddressDTO())),
      ...getPropsFromDTO('managedBy.address', Object.keys(new AddressDTO())),
      ...getPropsFromDTO('managedBy', Object.keys(new CompanyFullDTO())),
    ];

    for (const col of allColumns) {
      const theCol = this.columnDefaults.find(defCol => defCol.id === col);
      if (theCol) {
        this.columns.push({ ...theCol });
      } else if (!col.startsWith('_')) {
        this.columns.push({ id: col, title: col, selected: false, type: 'PROPERTY' });
      }
    }

    this.columns.push(
      ...createAttributeColumns(attributeMap.locationAttributes, 'LOCATION_ATTRIBUTE'),
      ...createAttributeColumns(attributeMap.companyAttributes, 'COMPANY_ATTRIBUTE')
    );

    if (preselectedColumns) {
      // if there are preselectedColumns by the user then Ids should be matching and those shall be selected
      preselectedColumns.forEach(preCol => {
        const matchingCol = this.columns.find(col => isColumnMatch(col, preCol));
        if (matchingCol) {
          matchingCol.selected = true;
          this.selectedColumns.push(matchingCol);
        }
      });
    } else {
      this.selectedColumns = this.columns.filter(column => column.selected);
    }

    this.initColumnFilterMap('locationFilter');

    this.applyColumns();
  }

  applyColumns() {
    this.allOrderOptions = this.refreshAllOrderOptions();
    this.saveSelectedColumns('locationListSelectedColumnsV1');
    this.refreshColumnFilters();
  }

  // selection methods

  selectLocation(location: LocationFullDTOAndTagsAndButtons) {
    const currentlySelectedLocations = this.selectedLocations();
    if (currentlySelectedLocations.has(location.id)) {
      currentlySelectedLocations.delete(location.id);
      this.selectedLocations.set(new Map(currentlySelectedLocations));
    } else {
      currentlySelectedLocations.set(location.id, location);
      this.selectedLocations.set(new Map(currentlySelectedLocations));
    }
  }

  selectAction($event: MultiSelectButtonAction) {
    const currentLocations = this.locations();
    if (!currentLocations || currentLocations.length === 0) {
      return;
    }
    const currentlySelectedLocations = this.selectedLocations();
    switch ($event) {
      case 'selectPage':
        currentLocations.forEach(item => {
          currentlySelectedLocations.set(item.id, item);
        });
        this.selectedLocations.set(new Map(currentlySelectedLocations));
        break;
      case 'deselectAll':
        this.deselectLocations();
        break;
      case 'deselectPage':
      default:
        currentLocations.forEach(item => {
          currentlySelectedLocations.delete(item.id);
        });
        this.selectedLocations.set(new Map(currentlySelectedLocations));
        break;
    }
  }

  returnSelectedLocation() {
    this.modalNavigation.result(Array.from(this.selectedLocations().values()));
  }

  deselectLocations() {
    this.selectedLocations.set(new Map());
  }

  // filter panel methods
  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.updateColumnFilters();
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    // property filter
    this.hasDevices = filterMap.get('hasDevices')?.[0];
    this.hasManager = filterMap.get('hasManager')?.[0];
    this.hasPoint = filterMap.get('hasPoint')?.[0];
    // status filter
    this.online = filterMap.get('online')?.[0];
    this.mixed = filterMap.get('mixed')?.[0];
    this.offline = filterMap.get('offline')?.[0];
    this.unknown = filterMap.get('unknown')?.[0];
    // Id filter
    this.filteredSchedules = filterMap.get('schedules');
    this.filteredManagers = filterMap.get('managedBy');
    this.filteredCulls = filterMap.get('playerCulls');
    // search
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }

  private updateColumnFilters(): void {
    // order
    this.orderOption.set({ ...this.filterPanelOrderOption() });
    // columnFilter
    if (this.removableColumnFilters?.length > 0) {
      this.removableColumnFilters.forEach(filter => this.columnFiltersMap.delete(filter));
    }
    if (this.isInModalContext) {
      return;
    }
    this.saveColumnFilterToQueryParam('locationFilter');
  }
}
